import { configureStore } from '@reduxjs/toolkit';
import type { TypedUseSelectorHook } from 'react-redux';
import { useDispatch, useSelector } from 'react-redux';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import governmentIntakeReducer from '@/modules/governmentIntake/state/governmentIntakeSlice';
import smeOnboardingReducer from '@/modules/smeOnboarding/state/smeOnboardingSlice';
import accountReducer from './Account';
import authReducer from './Auth';
import developerApplicationReducer from './DeveloperApplication';
import notificationReducer from './Notification';
import opportunityReducer from './Opportunity';
import projectReducer from './ProjectOld';
import projectsReducer from './Projects';
import toastReducer from './Toast';
import workspace from './Workspace';

// Redux Persist configuration for SME Onboarding
const smeOnboardingPersistConfig = {
  key: 'smeOnboarding',
  storage,
  whitelist: [
    'authToken',
    'emailVerified',
    'onboardingId',
    'initialInfo',
    'flowType',
    'demoScheduling',
    'organizationRegistration',
    'singleProjectRegistration',
    'assessmentData',
    'pricingSummary',
    'singleProjectData',
    'billingContact',
    'applicationSettings',
    'projectTemplates',
    'currentStep',
    'completedSteps',
  ],
};

const persistedSmeOnboardingReducer = persistReducer(smeOnboardingPersistConfig, smeOnboardingReducer);

// Redux Persist configuration for Government Intake
const governmentIntakePersistConfig = {
  key: 'governmentIntake',
  storage,
  whitelist: ['intakePayload', 'currentStep', 'completedSteps', 'intakeId'],
};

const persistedGovernmentIntakeReducer = persistReducer(governmentIntakePersistConfig, governmentIntakeReducer);

export const store = configureStore({
  reducer: {
    project: projectReducer,
    auth: authReducer,
    account: accountReducer,
    notification: notificationReducer,
    opportunity: opportunityReducer,
    projects: projectsReducer,
    toast: toastReducer,
    developerApplication: developerApplicationReducer,
    workspace,
    smeOnboarding: persistedSmeOnboardingReducer,
    governmentIntake: persistedGovernmentIntakeReducer,
  },
  middleware: (getDefaultMiddleware) => getDefaultMiddleware({ serializableCheck: false }),
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export const authSelector = (state: RootState) => state.auth;
export const workspaceSelector = (state: RootState) => state.workspace;
export const accountSelector = (state: RootState) => state.account;
export const notificationSelector = (state: RootState) => state.notification;
export const projectSelector = (state: RootState) => state.project;
export const projectsSelector = (state: RootState) => state.projects;
export const opportunitySelector = (state: RootState) => state.opportunity;
export const toastSelector = (state: RootState) => state.toast;
export const developerApplicationSelector = (state: RootState) => state.developerApplication;
export const governmentIntakeSelector = (state: RootState) => state.governmentIntake;
